import { Button } from "@components/ui/Button"
import { Icon } from "@components/ui/Icon"
import { Spinner } from "@components/ui/Spinner"
import { useFlyingCarpet } from "@features/flying-carpet/useFlyingCarpet"
import * as D from "@radix-ui/react-dialog"
import { cn, cx, tv } from "@utils/tw"
import { EditableText } from "eddev/blocks"
import { useRoute, useRouter } from "eddev/routing"
import { AnimatePresence, motion } from "motion/react"
import { useEffect, useRef, useState } from "react"
import { SFFRagDocumentTypeName } from "../../server/search/rag"
import { SearchInput } from "./SearchInput"
import { SearchResultsDisplay } from "./SearchResultsDisplay"
import { useSearchResults } from "./useSearchResults"
import { useSearch } from "./useSearchInput"

type Props = {}

export function SearchBoxMobile(props: Props) {
  const ref = useRef<HTMLInputElement>(null!)

  const carpet = useFlyingCarpet()
  const router = useRouter()
  const route = useRoute()

  const isSearchPage = route.view === "search-results"
  const [types, setTypes] = useState<SFFRagDocumentTypeName[]>([])
  const [isOpen, setIsOpen] = useState(false)

  const search = useSearch()

  const searchResults = useSearchResults({
    enabled: isOpen && !isSearchPage,
    isMobile: true,
    isQuickSearch: false,
    types: types,
  })

  useEffect(() => {
    if (carpet.isOpen) {
      setIsOpen(false)
    }
  }, [carpet.isOpen])

  const close = () => {
    setIsOpen(false)
    ref.current.blur()
  }

  useEffect(() => {
    setIsOpen(false)
  }, [route.pathname])

  const styles = createStyles({
    hasValue: search.input.inputQuery != "",
    open: isOpen,
    loading: search.loading,
  })

  return (
    <D.Root open={isOpen && !isSearchPage} onOpenChange={setIsOpen} modal>
      <div>
        <motion.form
          className={styles.base()}
          onSubmit={(e) => {
            e.preventDefault()
            e.currentTarget.focus()
          }}
          data-mixpanel-area="Search Panel"
        >
          <div
            onFocus={() => {
              setIsOpen(true)
            }}
          >
            <motion.div
              variants={{
                open: {},
                closed: {},
              }}
              initial="closed"
              animate={isOpen && !isSearchPage ? "open" : "closed"}
              className={styles.dialog()}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 40,
                restDelta: 0.001,
                restSpeed: 0.001,
              }}
            >
              {/* Mobile search bar */}
              <motion.div className="relative">
                <motion.div className="flex items-center">
                  {/* Search input */}
                  <motion.div className={styles.inputContainer()}>
                    <Icon name="search" />
                    <SearchInput
                      ref={ref}
                      className={styles.input()}
                      disabled={env.admin}
                      enterKeyHint="search"
                      onPointerDown={(e) => {
                        setIsOpen(true)
                      }}
                    />
                    <motion.div className="size-icon flex items-center justify-center">
                      <Spinner className={styles.spinner()} />
                      {/* <Icon name="load" className={styles.spinner()} /> */}
                    </motion.div>
                    <EditableText
                      className={cn(styles.placeholder(), env.admin ? "!opacity-100 !pointer-events-auto" : "")}
                      store="searchPlaceholder"
                      defaultValue="Search films, venues, guests..."
                    />
                  </motion.div>
                  {/* Close button */}
                  <AnimatePresence>
                    {isOpen && !isSearchPage && (
                      <motion.div
                        variants={{
                          open: { opacity: 1, width: "auto" },
                          closed: { opacity: 0, width: 0, transition: {} },
                        }}
                        initial="closed"
                        animate="open"
                        exit="closed"
                      >
                        <Button
                          variant="outline"
                          shape="icon"
                          aria-label="Close Search"
                          icon="cross"
                          onClick={() => {
                            close()
                            search.input.set("")
                          }}
                          className="ml-3"
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
                <motion.div
                  variants={{ open: { opacity: 1 }, closed: { opacity: 0, transition: { delay: 0.1 } } }}
                  className={styles.dropdown()}
                >
                  <motion.div
                    variants={{
                      open: { opacity: 1, y: 0 },
                      closed: { opacity: 0, y: -50 },
                    }}
                    className={cx(styles.contents(), "small-results")}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 40,
                      restDelta: 0.001,
                      restSpeed: 0.001,
                    }}
                  >
                    {!isSearchPage && (
                      <>
                        <SearchResultsDisplay types={types} setTypes={setTypes} search={searchResults} isSmall />
                        <div className="h-[8px]" />
                      </>
                    )}
                  </motion.div>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </motion.form>
      </div>
    </D.Root>
  )
}

const createStyles = tv({
  base: "w-full justify-center",
  slots: {
    dialog: ["justify-center outline-none"],
    inputContainer: [
      "flex relative px-3 lg:px-2 items-center rounded-md flex-1",
      "bg-bg-mid/60 has-[input:focus]:bg-bg-mid/90 transition-colors",
    ],
    input: [
      "max-sm:[font-size:16px] p-2 appearance-none bg-transparent border-none flex-1 ring-none h-control",
      "outline-none",
      "no-focus-scroll-ios",
    ],
    placeholder: [
      "text-fg absolute inset-0 flex items-center justify-center px-3 text-nowrap pointer-events-none",
      "opacity-1 transition-opacity",
    ],
    spinner: "transition-opacity opacity-0",
    dropdown:
      "fixed z-[90] left-0 right-0 top-headerHeight overflow-hidden h-[calc(100lvh+200px)] bg-bg outline-none pointer-events-none",
    contents: [
      // "max-h-[calc(var(--visual-viewport-height,100vh)-var(--spacing-headerHeight))]",
      "absolute top-0 right-0 left-0 h-[calc(100dvh-var(--spacing-headerHeight))] overflow-scroll overflow-x-hidden overscroll-contain outline-none",
      "pb-[calc(100dvh-var(--visual-viewport-height,100vh))]",
      "px-[18px]",
    ],
  },
  variants: {
    hasValue: {
      true: {
        placeholder: "opacity-0",
      },
    },
    open: {
      true: {
        inputContainer: "has-[input:focus]:bg-bg-mid/90",
        dropdown: "pointer-events-auto",
      },
    },
    loading: {
      true: {
        spinner: "opacity-100",
      },
    },
  },
})
